import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup'; // Import Yup
import { useValidation } from '../../hooks/useValidation';
import styles from './RegistrationForm.module.css';
import { coreService } from '../../services/coreService';
import * as Step from '../../config/stepNumbers';
import CustomerInformation from './steps/CustomerInformation/CustomerInformation'; // Customer Information
import AddUsers from './steps/AddUsers/AddUsers'; // Add Users
import Products from './steps/Products/Products'; // Addons
import EstimatedTotal from './steps/EstimatedTotal/EstimatedTotal'; //
import Payment from './steps/Payment/Payment'; // Payment Information
import Agreements from './steps/Agreements/Agreements'; // Terms and Conditions
import Confirmation from './steps/Confirmation/Confirmation'; // Confirmation
import SuccessPage from '../../pages/SuccessPage/SuccessPage'; // Success Splash
import {
  REACT_APP_EMR_COMPONENT_HANDLE, REACT_APP_ON_CALL_PROVIDERS_COMPONENT_HANDLE
} from "../../env";
import {
  calculateEstimatedTotal, formatSelection, processReferralComponents, formatPhoneNumber
} from './registrationUtils';

// Persistence constants (module-level to avoid dependency issues)
const PERSIST_VERSION = 'v1';
const PERSIST_BASE_KEY = `registration_form_state_${PERSIST_VERSION}`;
// Before the step swap, PAYMENT_STEP was 5; now it's 6. We check both to handle old persisted state.
const OLD_PAYMENT_STEP = 5;

const formSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Must be a valid email').required('Email is required'),
  phone: Yup.string().matches(
    /^\(\d{3}\) \d{3}-\d{4}$/,
    'Must be a valid phone number (XXX) XXX-XXXX'
  ).required('Phone number is required'),
  org_name: Yup.string().required('Organization name is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  zip: Yup.string().required('ZIP/Postal code is required').test('zip-validation', 'Invalid ZIP/Postal code', function(value) {
    const { country } = this.parent;
    if (country === 'US') {
      return /^[0-9]{5}(?:-[0-9]{4})?$/.test(value);
    } else if (country === 'CA') {
      return /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/i.test(value);
    } else if (country === 'MX') {
      return /^\d{5}$/.test(value);
    }
    return true;
  }),
});

const agreementsSchema = Yup.object({
  termsOfUse: Yup.boolean().oneOf([true], 'You must accept the Terms of Use.'),
  businessAssociate: Yup.boolean().oneOf([true], 'You must accept the Business Associate Agreement.'),
  userAgreement: Yup.boolean().oneOf([true], 'You must accept the User Agreement.'),
  servicesAgreement: Yup.boolean().oneOf([true], 'You must accept the Services Agreement.'),
  privacyPolicy: Yup.boolean().oneOf([true], 'You must accept the Privacy Policy.'),
  name: Yup.string().required('Your name is required to confirm'),
});

function RegistrationForm({ referralCode }) {
  // Form State
  const [step, setStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1); // for clickable header
  const [isLoading, setIsLoading] = useState(true);
  const [referralLoaded, setReferralLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [agreementsErrors, validateAgreements] = useValidation(agreementsSchema);
  const [formErrors, validateForm] = useValidation(formSchema);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', org_name: '', address: '', address_2: '', city: '', state: '', country: 'US', zip: '' });
  const [agreementsData, setAgreementsData] = useState({ termsOfUse: false, businessAssociate: false, userAgreement: false, servicesAgreement: false, privacyPolicy: false, name: ''});

  // AddOn / User State
  const [addOns, setAddOns] = useState([]);     // all add-ons available to the user
  const [selectedAddOns, setSelectedAddOns] = useState([]); // add-ons user has chosen
  const [users, setUsers] = useState([]);
  const [licensingFee, setLicensingFee] = useState(false);
  const [emrChoice, setEmrChoice] = useState('no');
  const [estimatedTotal, setEstimatedTotal] = useState(0);
  // Agreements / Terms and Conditions State.
  // The individual acceptances and the signer name live in `agreementsData`
  // (the single source of truth); we only track when they were accepted.
  const [timeAccepted, setTimeAccepted] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Chargify / Payment
  const [token, setToken] = useState(null);
  const paymentRef = useRef();

  const totalSteps = Step.TOTAL_STEPS;

  // Reactive per-step validity, used to gate the Continue button.
  // Steps we can validate client-side: Customer Info, Users, Agreements.
  // Products/Estimated Total have sensible defaults; Payment can't be
  // validated before Chargify tokenizes, so those stay enabled.
  const isCustomerInfoValid = useMemo(() => formSchema.isValidSync(formData), [formData]);
  const hasManager = useMemo(() => users.some(user => !!user?.user_type?.is_manager), [users]);
  const isAgreementsValid = useMemo(() => agreementsSchema.isValidSync(agreementsData), [agreementsData]);

  const canProceed = (() => {
    switch (step) {
      case Step.CUSTOMER_INFORMATION_STEP: return isCustomerInfoValid;
      case Step.USERS_STEP: return hasManager;
      case Step.AGREEMENTS_STEP: return isAgreementsValid;
      default: return true;
    }
  })();

  //==============================================================================================================================================
  // Persistence (exclude Payment step)
  const namespacedKey = `${PERSIST_BASE_KEY}_${referralCode || 'default'}`;

  const safeParse = (raw) => {
    try { return JSON.parse(raw); } catch { return null; }
  };

  // Ensure we only hydrate once per referral namespace to avoid clobbering user input
  const hydratedKeyRef = useRef(null);

  // Hydrate from localStorage when the referralCode (namespace) is known/changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hydratedKeyRef.current === namespacedKey) return; // avoid double hydration for same key

    // Prefer the namespaced key if present; otherwise fall back to "default" and migrate
    const defaultKey = `${PERSIST_BASE_KEY}_default`;
    const existingNamespaced = window.localStorage.getItem(namespacedKey);
    const existingDefault = window.localStorage.getItem(defaultKey);

    let raw = null;
    let usedKey = null;
    if (existingNamespaced) {
      raw = existingNamespaced;
      usedKey = namespacedKey;
    } else if (existingDefault) {
      raw = existingDefault;
      usedKey = defaultKey;
    }

    const saved = safeParse(raw);
    if (!saved) {
      hydratedKeyRef.current = namespacedKey;
      return;
    }

    // Restore basic shapes defensively
    if (saved.formData && typeof saved.formData === 'object') setFormData(prev => ({ ...prev, ...saved.formData }));
    if (saved.agreementsData && typeof saved.agreementsData === 'object') setAgreementsData(prev => ({ ...prev, ...saved.agreementsData }));
    if (Array.isArray(saved.users)) setUsers(saved.users);
    if (Array.isArray(saved.selectedAddOns)) setSelectedAddOns(saved.selectedAddOns);
    if (typeof saved.emrChoice === 'string') setEmrChoice(saved.emrChoice);
    if (typeof saved.estimatedTotal === 'number') setEstimatedTotal(saved.estimatedTotal);
    if (typeof saved.timeAccepted === 'string') setTimeAccepted(saved.timeAccepted);

    // Step handling: never restore directly into Payment step (check both old and new step values)
    if (typeof saved.step === 'number') {
      const restoredStep = (saved.step === Step.PAYMENT_STEP || saved.step === OLD_PAYMENT_STEP)
        ? Step.AGREEMENTS_STEP
        : saved.step;
      setStep(Math.min(Math.max(restoredStep, 1), totalSteps));
    }
    if (typeof saved.maxStepReached === 'number') setMaxStepReached(saved.maxStepReached);

    // If we loaded from the default key and we now have a namespace, migrate it
    try {
      if (usedKey === defaultKey && namespacedKey !== defaultKey) {
        window.localStorage.setItem(namespacedKey, raw);
        window.localStorage.removeItem(defaultKey);
      }
    } catch {}

    hydratedKeyRef.current = namespacedKey;
  }, [namespacedKey, totalSteps]);

  // Clear errors when step changes
  useEffect(() => {
    setError(null);
  }, [step]);

  // Persist state when it changes, except while on Payment step
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hydratedKeyRef.current !== namespacedKey) return; // wait until hydration for CURRENT key completes to avoid overwriting
    if (step === Step.PAYMENT_STEP) return; // Do not persist while on CC capture screen

    const payload = {
      version: PERSIST_VERSION,
      savedAt: new Date().toISOString(),
      step,
      maxStepReached,
      formData,
      agreementsData,
      users,
      selectedAddOns,
      emrChoice,
      // estimatedTotal can be recomputed, but included for convenience
      estimatedTotal,
      // when the user accepted the agreements (used for signed_at)
      timeAccepted,
    };

    // Debounce saves slightly to avoid excessive writes
    const handle = setTimeout(() => {
      try {
        window.localStorage.setItem(namespacedKey, JSON.stringify(payload));
      } catch (e) {
        // Ignore quota or serialization errors
      }
    }, 200);

    return () => clearTimeout(handle);
  }, [step, maxStepReached, formData, agreementsData, users, selectedAddOns, emrChoice, estimatedTotal, timeAccepted, namespacedKey]);

  // Navigation Handlers
  const nextStep = async () => {
    if (step === Step.CUSTOMER_INFORMATION_STEP) {
      const isFormValid = await validateForm(formData);
      if (!isFormValid) {
        setError("Please correct the errors in the form.");
        return;
      }
    } else if (step === Step.USERS_STEP) {
      const managerCount = users.filter(user => !!user?.user_type?.is_manager).length;

      if (managerCount === 0) {
        setError("At least 1 user must have 'Manager' user type!");
        return;
      }
    } else if (step === Step.AGREEMENTS_STEP) {
      const isFormValid = await validateAgreements(agreementsData);
      if (!isFormValid) {
        setError("Please correct the errors in the form.");
        return;
      }
      setTimeAccepted(new Date().toISOString());
    } else if (step === Step.PAYMENT_STEP) {
      if (paymentRef.current) {
        setIsProcessing(true);
        setError(null);
        try {
          const token = await paymentRef.current.tokenize();
          setToken(token);
        } catch (error) {
          console.error("Tokenization failed", error);
          setError(error.errors ? error.errors.join(", ") : (error.message || "Payment tokenization failed."));
          setIsProcessing(false);
          return;
        }
        setIsProcessing(false);
      }
    }
    setStep(prevStep => Math.min(prevStep + 1, totalSteps));
  };

  const prevStep = () => { setStep(prevStep => Math.max(prevStep - 1, 1)); };

  const goToStep = (stepNumber) => {
    if (stepNumber > 0 && stepNumber <= totalSteps) {
      setStep(stepNumber);
    }
  }

  useEffect(() => {
    if (step > maxStepReached) {
      setMaxStepReached(step);
    }
  },[step, maxStepReached]);

  //==============================================================================================================================================
  // Customer Information Handlers

  const handleCustomerInformationChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = formatPhoneNumber(value);
    }

    if (name === 'country') {
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
        state: '',
        zip: ''
      }));
    } else {
      setFormData(prevData => ({ ...prevData, [name]: value }));
    }
  };

  //==============================================================================================================================================
  // Users Handlers

  // track number of on-call users and update state with quantity = count
  useEffect(() => {
    if (addOns.length === 0) return;

    const onCallAddOn = addOns.find((p) => p.handle === REACT_APP_ON_CALL_PROVIDERS_COMPONENT_HANDLE);

    if (!onCallAddOn) return;

    const onCallProviderCount = users.filter(user => user?.user_type?.is_on_call_provider).length;

    setSelectedAddOns((currentSelected) => {
      const otherSelectedAddons = currentSelected.filter((p) => p.handle !== REACT_APP_ON_CALL_PROVIDERS_COMPONENT_HANDLE);

      if (onCallProviderCount > 0) {
        return [
          ...otherSelectedAddons,
          { ...formatSelection(onCallAddOn, onCallProviderCount) },
        ]
      }

      return otherSelectedAddons;
    });
  }, [users, addOns]);

  const handleAddUser = (newUser) => {
    setUsers(currentUsers => [
      ...currentUsers,
      { ...newUser, id: Date.now() }
    ]);
  };

  const handleUpdateUser = (updatedUser) => {
    setUsers(currentUsers => currentUsers.map(user => user.id === updatedUser.id ? updatedUser : user));
  };

  const handleDeleteUser = (id) => {
    setUsers(currentUsers => currentUsers.filter(user => user.id !== id));
  };

  //==============================================================================================================================================
  // Addon Handlers

  // Track estimated total
  useEffect(() => {
    const total = calculateEstimatedTotal(selectedAddOns, addOns);
    setEstimatedTotal(total);
  }, [selectedAddOns, addOns]);


  // addon selection helper
  const handleAddOnSelection = (addOn, wantsAddOn) => {
    setSelectedAddOns(current => {
      const selected = current.some(p => p.id === addOn.id);

      if (wantsAddOn && !selected) {
        const formatted = formatSelection(addOn, 1);
        return [...current, formatted];
      }

      if (!wantsAddOn && selected) {
        return current.filter(p => p.id !== addOn.id);
      }

      return current;
    })
  }

  // Handle user selects "Yes" for an addon
  const handleAddOnSelectionChange = (addOn, wantsAddOn) => {
    handleAddOnSelection(addOn, wantsAddOn);
  }

  // handle special EMR component
  const handleEmrChange = (e) => {
    const choice = e.target.value;
    setEmrChoice(choice);

    if (addOns.length === 0) return;

    const emrAddOn = addOns.find(a => a.handle === REACT_APP_EMR_COMPONENT_HANDLE);
    if (!emrAddOn) return;

    setSelectedAddOns(currentSelected => {
      const otherSelectedAddons = currentSelected.filter(p => p.handle !== REACT_APP_EMR_COMPONENT_HANDLE);

      if (choice !== 'no') {
        const formattedEmrAddon = {
          ...formatSelection(emrAddOn, 1),
          meta: {
            "provider": choice,
          },
        };
        return [...otherSelectedAddons, formattedEmrAddon];
      }

      return otherSelectedAddons;
    });
  }


  // fetch data by referral
  useEffect(() => {
    if (!referralCode) {
      setError("No referral code provided.");
      return;
    }

    const fetchAndProcessAddOns = async () => {
      setIsLoading(true);
      try {
        const referralData = await coreService.getReferralByCode(referralCode);

        if (referralData && Array.isArray(referralData.components)) {
          const processedData = processReferralComponents(referralData.components, referralData.pricePoints || []);
          setAddOns(processedData.addOns);
          setLicensingFee(processedData.licensingFee);
          setReferralLoaded(true);
          setError(null);
        } else {
          throw new Error("Invalid data format from referral code.");
        }
      } catch (err) {
        console.error("Failed to fetch or process referral data:", err);
        setError(err.message || "Failed to load add-on data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessAddOns();
  }, [referralCode]);

  //==============================================================================================================================================
  // Terms and conditions Handlers

  const handleAgreementsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAgreementsData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  //==============================================================================================================================================
  // Submission

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === totalSteps && token) {
      setIsProcessing(true);
      setError(null);
      try {
        const registration = {
          registration: {
            referral_code: referralCode,
            chargify_token: token,
            data: {
              customer_information: formData,
              users: users,
              add_ons: selectedAddOns,
              estimated_total: estimatedTotal,
              terms_and_conditions: {
                terms_of_use: agreementsData.termsOfUse,
                business_associate_agreement: agreementsData.businessAssociate,
                user_agreement: agreementsData.userAgreement,
                services_agreement: agreementsData.servicesAgreement,
                privacy_policy: agreementsData.privacyPolicy,
                signed_by: agreementsData.name,
                signed_at: timeAccepted || new Date().toISOString()
              }
            },
          }
        };

        const response = await coreService.createRegistration(registration);

        if (response.code === 201) {
          // Wipe any persisted state upon full submission success
          try {
            if (typeof window !== 'undefined') {
              const defaultKey = `${PERSIST_BASE_KEY}_default`;
              window.localStorage.removeItem(namespacedKey);
              window.localStorage.removeItem(defaultKey);
            }
          } catch {}
          setIsSubmitted(true);
        } else if (response.code === 400) {
          setError('Invalid or missing input');
        } else if (response.code === 500) {
          setError('Unexpected server-side failure');
        } else {
          setError(response.data?.errors?.[0]?.message || 'An unexpected error occurred.');
        }
      } catch (error) {
        console.error('Registration Error:', error);
        setError(error.message || 'Failed to submit registration.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      console.error('Registration failed: No payment token available');
      setError('Payment information missing, please return to Payment Information step.')
    }
  };

  if (isLoading) {
    return <div className={styles.loadingState}>Loading Add-Ons...</div>;
  }

  // Fatal errors (e.g. failing to load referral data) still block the whole app
  const isFatalError = error && !referralLoaded;
  if (isFatalError && !isSubmitted) {
    return <div className={styles.errorState}>Error: {error}</div>;
  }

  if (isSubmitted) {
    return (
      <div className={styles.formLayoutWrapper}>
        <div className={styles.formContainer}>
          <SuccessPage />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formLayoutWrapper}>
      <div className={styles.formContainer}>
        <div className={styles.stepTracker}>
          {[...Array(totalSteps)].map((_, index) => {
            const stepNumber = index + 1;
            const isClickable = stepNumber <= maxStepReached && step !== stepNumber;

            return (
              <React.Fragment key={stepNumber}>
                <div
                  className={`
                    ${styles.step} 
                    ${step === stepNumber ? styles.activeStep : ''} 
                    ${step > stepNumber ? styles.completedStep : ''}
                    ${isClickable ? styles.clickableStep : ''}
                  `}

                  onClick={isClickable && !isProcessing ? () => goToStep(stepNumber) : undefined}
                  onKeyDown={isClickable && !isProcessing ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToStep(stepNumber);
                    }
                  } : undefined}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable && !isProcessing ? 0 : undefined}
                  aria-label={`Step ${stepNumber}`}
                  aria-current={step === stepNumber ? 'step' : undefined}
                >
                  {step > stepNumber ? '✓' : stepNumber}
                </div>
                {stepNumber < totalSteps && (
                  <div className={`${styles.stepConnector} ${step > stepNumber ? styles.stepConnectorFilled : ''}`}></div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div className={styles.inlineError}>
            {error}
            <span className={styles.inlineErrorClose} onClick={() => setError(null)}>&times;</span>
          </div>
        )}

        <div className={styles.formContent}>
          {step === Step.CUSTOMER_INFORMATION_STEP && <CustomerInformation formData={formData} handleChange={handleCustomerInformationChange} errors={formErrors} />}
          {step === Step.USERS_STEP && <AddUsers users={users} onUserAdd={handleAddUser} onUserDelete={handleDeleteUser} onUserUpdate={handleUpdateUser} licensingFee={licensingFee} />}
          {step === Step.SELECT_PRODUCTS_STEP && <Products selectedAddOns={selectedAddOns} emrChoice={emrChoice} onEmrChange={handleEmrChange} addOns={addOns} onAddOnSelectionChange={handleAddOnSelectionChange} />}
          {step === Step.ESTIMATED_TOTAL_STEP && <EstimatedTotal selectedAddOns={selectedAddOns} estimatedTotal={estimatedTotal} addOns={addOns}/>}
          {step === Step.PAYMENT_STEP && <Payment ref={paymentRef} formData={formData} setGlobalError={setError} />}
          {step === Step.AGREEMENTS_STEP && <Agreements agreementsData={agreementsData} handleChange={handleAgreementsChange} errors={agreementsErrors} />}
          {step === Step.CONFIRMATION_STEP && (
            <Confirmation 
              formData={formData} 
              addOns={selectedAddOns} 
              users={users} 
              onSubmit={handleSubmit} 
              goToStep={goToStep} 
              isSubmitting={isProcessing}
            />
          )}
        </div>

        {(step > Step.CUSTOMER_INFORMATION_STEP || step < totalSteps) && (
          <div className={styles.navFooter}>
            {step > Step.CUSTOMER_INFORMATION_STEP && (
              <button type="button" className={styles.backButton} onClick={prevStep} disabled={isProcessing}>
                &larr; Back
              </button>
            )}
            {step < totalSteps && (
              <button
                type="button"
                className={`${styles.continueButton} ${!canProceed ? styles.continueButtonInactive : ''}`}
                onClick={nextStep}
                disabled={isProcessing}
                title={!canProceed ? 'Complete the required fields on this step to continue' : undefined}
              >
                {isProcessing ? '...' : 'Continue →'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RegistrationForm;
